import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo, Breadcrumbs, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { Reveal, SectionTag } from "@/components/Motion";
import { ChatButton, FinalCta } from "@/components/Cta";
import FaqAccordion from "@/components/FaqAccordion";
import { BLOG_POSTS } from "@/data/blog";
import NotFound from "@/pages/NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  const crumbs = [
    { name: "首页", path: "/" },
    { name: "电商资讯", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ];

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${post.title} | TKShopMY`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        schemas={[
          breadcrumbSchema(crumbs),
          faqSchema(post.faqs),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            author: { "@type": "Organization", name: "TKShopMY", url: "https://tkshopmy.com" },
            publisher: { "@id": "https://tkshopmy.com/#organization" },
            datePublished: post.date,
            dateModified: post.updated,
            mainEntityOfPage: `https://tkshopmy.com/blog/${post.slug}`,
          },
        ]}
      />
      <article className="pt-28 md:pt-40 pb-16 md:pb-24" data-testid="blog-post">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Breadcrumbs items={crumbs} />
          <Reveal>
            <h1 className="font-display text-4xl font-medium leading-[1.12] text-balance sm:text-5xl">
              {post.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink/50">
              <span>作者：<span className="font-semibold text-ink/70">TKShopMY 团队</span></span>
              <span>发布于 {new Date(post.date).toLocaleDateString("zh-Hans", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span>更新于 {new Date(post.updated).toLocaleDateString("zh-Hans", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span>{post.readTime}</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <nav className="mt-10 rounded-2xl border border-ink/10 bg-white p-6" aria-label="Table of contents" data-testid="blog-toc">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">本文目录</p>
              <ol className="mt-3 space-y-2">
                {post.sections.map((s, i) => (
                  <li key={s.h}>
                    <a href={`#section-${i}`} className="text-sm text-ink/65 underline-offset-4 hover:text-forest hover:underline">
                      {i + 1}. {s.h}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </Reveal>

          <div className="mt-12 space-y-12">
            {post.sections.map((s, i) => (
              <Reveal key={s.h} delay={0.05}>
                <section id={`section-${i}`} className="scroll-mt-28">
                  <h2 className="font-display text-2xl font-semibold leading-snug sm:text-3xl">
                    <span className="mr-3 font-light italic text-moss">0{i + 1}</span>
                    {s.h}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-ink/70">{s.p}</p>
                </section>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 rounded-2xl bg-forest p-8 text-paper md:flex md:items-center md:justify-between" data-testid="blog-inline-cta">
              <div>
                <h2 className="font-display text-2xl font-semibold">想了解自己的产品适合怎样开始？</h2>
                <p className="mt-2 text-sm text-paper/70">无论是开店、运营，还是一件代发 / 无需自行囤货合作方式，都可以先在线咨询。</p>
              </div>
              <div className="mt-5 shrink-0 md:mt-0">
                <ChatButton location={`blog-${slug}`} variant="lime" testId="blog-chat-button">
                  WhatsApp 咨询我们
                </ChatButton>
              </div>
            </div>
          </Reveal>

          <div className="mt-16">
            <SectionTag>本文常见问题</SectionTag>
            <FaqAccordion faqs={post.faqs} testIdPrefix="blog-faq" />
          </div>

          <div className="mt-16">
            <SectionTag>继续阅读</SectionTag>
            <div className="space-y-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group flex items-center justify-between gap-4 rounded-xl border border-ink/10 bg-white px-5 py-4 transition-colors hover:border-forest/30"
                >
                  <span className="text-sm font-semibold text-ink group-hover:text-forest">{r.title}</span>
                  <ArrowRight size={15} className="shrink-0 text-ink/40 transition-transform group-hover:translate-x-1 group-hover:text-forest" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
      <FinalCta />
    </>
  );
}
